import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Terminal, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { LogoShield } from "@/components/LogoShield";
import { usePromoConfig } from "@/hooks/use-promo-config";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { config: promoConfig } = usePromoConfig();

  const promoCopy = promoConfig.bannerText;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Websites", path: "/websites" },
    { name: "About", path: "/about" },
    { name: "Blog", path: "/blog" },
  ];

  // Hide banner completely
  const showBanner = false;

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {showBanner && promoConfig.enabled && (
        <div className="bg-accent text-accent-foreground border-b border-accent/40">
          <div className="container mx-auto px-4">
            <div className="flex flex-col gap-2 py-2 text-xs font-mono tracking-wide md:flex-row md:items-center md:justify-between">
              <span className="uppercase">{promoCopy}</span>
              <Link
                to={promoConfig.ctaHref}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest hover:opacity-90"
                aria-label={`${promoCopy} ${promoConfig.ctaLabel}`}
              >
                {promoConfig.ctaLabel}
              </Link>
            </div>
          </div>
        </div>
      )}
      <nav className="bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="relative">
                <LogoShield className="h-7 w-7 transition-all" />
                <div className="absolute inset-0 bg-primary/30 blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <span className="font-heading font-bold text-xl tracking-tight">
                <span className="text-foreground">Shadow</span>
                <span className="text-primary">Scan</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all",
                    isActive(link.path)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                >
                  {link.name}
                </Link>
              ))}
              {user ? (
                <Button asChild variant="outline" className="ml-2">
                  <Link to="/portal">
                    <User className="mr-2 h-4 w-4" />
                    Portal
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="ghost" className="ml-2">
                    <Link to="/portal/login">
                      Login
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="ml-2">
                    <Link to="/portal/sign-up">
                      Sign Up
                    </Link>
                  </Button>
                </>
              )}
              <Button asChild className="ml-2 bg-accent hover:bg-accent/90 text-accent-foreground font-medium">
                <Link to="/contact">
                  <Terminal className="mr-2 h-4 w-4" />
                  Free Scan
                </Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-foreground p-2 hover:bg-secondary rounded-md transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden py-4 space-y-2 border-t border-border animate-fade-in">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "block px-4 py-3 rounded-md font-medium transition-all",
                    isActive(link.path)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              {user ? (
                <Button asChild variant="outline" className="w-full mt-2">
                  <Link to="/portal" onClick={() => setIsOpen(false)}>
                    <User className="mr-2 h-4 w-4" />
                    Portal
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="ghost" className="w-full mt-2">
                    <Link to="/portal/login" onClick={() => setIsOpen(false)}>
                      Login
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full mt-2">
                    <Link to="/portal/sign-up" onClick={() => setIsOpen(false)}>
                      Sign Up
                    </Link>
                  </Button>
                </>
              )}
              <Button asChild className="w-full mt-2 bg-accent hover:bg-accent/90 text-accent-foreground">
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Terminal className="mr-2 h-4 w-4" />
                  Request Free Scan
                </Link>
              </Button>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default Navigation;
